import { useEffect } from "react";

const SITE = "https://woodlandjoinery.com";

/**
 * Lightweight SEO manager — mounts per-page and sets:
 *  - document.title
 *  - meta[name=description]
 *  - link[rel=canonical]
 *  - meta[property=og:title|og:description|og:url|og:image]
 *  - meta[name=twitter:*]
 *  - optional JSON-LD structured data blocks (kept in-sync per page)
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = `${SITE}/og-image.jpg`,
  jsonLd = [],
}) {
  useEffect(() => {
    const url = `${SITE}${path}`;

    document.title = title;

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Woodland Joinery and Building");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setLink("canonical", url);

    // Manage a bag of JSON-LD scripts, tagged so we don't leak across pages.
    const TAG = "data-seo-json";
    document.querySelectorAll(`script[${TAG}]`).forEach((s) => s.remove());
    jsonLd.forEach((obj, i) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute(TAG, `${i}`);
      el.text = JSON.stringify(obj);
      document.head.appendChild(el);
    });
  }, [title, description, path, image, JSON.stringify(jsonLd)]);

  return null;
}

function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/* -------------------- Shared structured data -------------------- */

export const LOCAL_BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  "@id": `${SITE}/#business`,
  name: "Woodland Joinery and Building",
  alternateName: "Woodland Joinery",
  description:
    "Bespoke joinery, extensions, decking, staircases, fitted furniture and general building work across Sheffield and the surrounding areas.",
  url: SITE,
  image:
    "https://customer-assets.emergentagent.com/job_green-joinery/artifacts/7zg5z0zf_21E4688E-75F7-4055-B92D-0D3F251D117A.png",
  logo:
    "https://customer-assets.emergentagent.com/job_green-joinery/artifacts/7zg5z0zf_21E4688E-75F7-4055-B92D-0D3F251D117A.png",
  telephone: "+44 7856 925823",
  email: "info@woodlandjoinery.co.uk",
  priceRange: "££",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Sheffield",
    addressRegion: "South Yorkshire",
    addressCountry: "GB",
  },
  areaServed: [
    { "@type": "City", name: "Sheffield" },
    { "@type": "City", name: "Rotherham" },
    { "@type": "City", name: "Chesterfield" },
    { "@type": "City", name: "Dronfield" },
    { "@type": "City", name: "Barnsley" },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      name: "Sean",
      telephone: "+44 7856 925823",
      contactType: "customer service",
      areaServed: "GB",
      availableLanguage: "English",
    },
    {
      "@type": "ContactPoint",
      name: "Rocky",
      telephone: "+44 7375 808008",
      contactType: "customer service",
      areaServed: "GB",
      availableLanguage: "English",
    },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "07:30",
      closes: "18:00",
    },
  ],
  sameAs: [],
  makesOffer: [
    "Bespoke Joinery",
    "Extensions and Renovations",
    "Decking and Outdoor Living",
    "Staircases",
    "Fitted Furniture",
    "General Building Work",
  ].map((n) => ({
    "@type": "Offer",
    itemOffered: { "@type": "Service", name: n, areaServed: "Sheffield" },
  })),
};

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: SITE,
  name: "Woodland Joinery and Building",
  publisher: { "@id": `${SITE}/#business` },
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});
